/**
 * Gemeinsame Zeitbasis für Sender und Viewer.
 *
 * Die Latenzanzeige vergleicht den Sendezeitpunkt eines Frames mit dem
 * Zeitpunkt seiner Anzeige – zwei Uhren auf zwei Rechnern, die beliebig weit
 * auseinanderliegen können. Ohne Abgleich ist die Zahl auf einer Maschine
 * zufällig richtig und über Rechnergrenzen hinweg wertlos.
 *
 * Beide Seiten rechnen deshalb in die Uhr des Servers um: ein Zeitstempel hin
 * und zurück, die halbe Laufzeit als Schätzung für den Hinweg. Behalten wird
 * die Probe mit der kürzesten Laufzeit – sie hat am wenigsten Wartezeit in
 * sich und schätzt den Hinweg am genauesten. Ohne Abgleich liefert `now()`
 * schlicht die eigene Uhr; die Anzeige bleibt dann so gut wie vorher.
 */
export class ServerClock {
  /** Serverzeit ≈ Date.now() + offset. */
  private offset = 0;
  private bestRoundTrip = Number.POSITIVE_INFINITY;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly send: (message: object) => void) {}

  now(): number {
    return Date.now() + this.offset;
  }

  get synced(): boolean {
    return Number.isFinite(this.bestRoundTrip);
  }

  /** Kürzeste gemessene Laufzeit hin und zurück. */
  get roundTrip(): number {
    return this.synced ? this.bestRoundTrip : 0;
  }

  /** Nach dem Verbindungsaufbau: erst mehrere Proben, danach selten nachziehen. */
  start(): void {
    this.stop();
    // Drei kurz nacheinander: Die erste läuft oft durch einen noch kalten
    // Socket und misst zu lang; behalten wird ohnehin nur die kürzeste.
    for (let i = 0; i < 3; i++) setTimeout(() => this.probe(), i * 250);
    this.timer = setInterval(() => this.probe(), 30_000);
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Vor einem neuen Verbindungsaufbau: Die bisherige Bestmarke gilt nicht mehr,
   * der Weg kann ein anderer sein. Der Versatz bleibt stehen – eine alte
   * Schätzung ist besser als gar keine, bis die erste neue Probe da ist.
   */
  reset(): void {
    this.bestRoundTrip = Number.POSITIVE_INFINITY;
  }

  /** Antwort des Servers auf eine Probe. */
  accept(message: { sent: number; server?: number }): void {
    if (typeof message.server !== 'number') return;
    const roundTrip = Date.now() - message.sent;
    if (roundTrip < 0 || roundTrip >= this.bestRoundTrip) return;
    this.bestRoundTrip = roundTrip;
    this.offset = message.server + roundTrip / 2 - Date.now();
  }

  private probe(): void {
    this.send({ t: 'time', sent: Date.now() });
  }
}
